import { Box, Typography } from '@mui/material';
import { CurveData } from '~/domain/patient/dvh';
import { relativeDosePercent } from '~/utils/dose/doseDisplayValidation';

/**
 * The dose to report for a hovered point, capped at its structure's maximum
 * as the maximum is rendered. The plotted doses already stay at or below the
 * structure's maximum, so the cap is normally inert. It exists because the
 * tooltip prints the hovered dose and the "Max" beside it as two separately
 * rounded numbers, and `toFixed` rounds up whenever the next digit is 5 or
 * more. A curve must never offer a dose above the maximum printed beside it.
 */
export function cappedDoseGy(doseGy: number, curve: CurveData): number {
    const shown = parseFloat(doseGy.toFixed(1));
    const shownMax = parseFloat(curve.doseMaxGy.toFixed(1));
    return shown > shownMax ? curve.doseMaxGy : doseGy;
}

/**
 * What the DVH tooltip says about one hovered point: the structure, its
 * dose in Gy and as a percentage of the normalization dose, both from one
 * capped value so they cannot disagree, its volume, and the structure's
 * dose statistics. The percentage is left out when it does not exist.
 */
export default function DvhTooltipContent({
    curve,
    doseGy,
    volumePercent,
    normalizationValueGy,
}: {
    curve: CurveData;
    doseGy: number;
    volumePercent: number;
    normalizationValueGy: number;
}) {
    const capped = cappedDoseGy(doseGy, curve);
    const relativePercent = relativeDosePercent(capped, normalizationValueGy);
    return (
        <>
            <Box style={{ display: 'flex', alignItems: 'center' }}>
                <Box
                    style={{
                        width: '12px',
                        height: '12px',
                        marginRight: '5px',
                        backgroundColor: curve.color,
                    }}
                />
                <Typography sx={{ fontWeight: 'bold' }}>
                    {curve.name}
                </Typography>
            </Box>
            <Typography>
                {relativePercent === undefined
                    ? `Dose: ${capped.toFixed(1)} Gy`
                    : `Dose: ${capped.toFixed(1)} Gy (${relativePercent.toFixed(1)} %)`}
            </Typography>
            <Typography>{`Volume: ${volumePercent.toFixed(1)} %`}</Typography>
            <Typography>
                {`Max: ${curve.doseMaxGy.toFixed(1)} Gy, Min: ${curve.doseMinGy.toFixed(1)} Gy, Mean: ${curve.doseMeanGy.toFixed(1)} Gy`}
            </Typography>
        </>
    );
}
